import { apiRequest, trickleCreateObject, trickleListObjects, trickleUpdateObject } from "./api";
import { getUserData } from "./auth";

// Credit system management
async function getUserCredits() {
    try {
        const userData = getUserData();
        if (!userData || !userData.id) {
            throw new Error('User not authenticated');
        }
        
        try {
            const userCreditRecord = await apiRequest('GET', '/api/index/getUserCredits');
            
            if (userCreditRecord) {
                return userCreditRecord.data.value
            }
            return 0;
        } catch (error) {
            console.error('Failed to access credit records:', error);
            return 0
        }
    } catch (error) {
        console.error('Failed to get user credits:', error);
        throw error;
    }
}

export async function startUseCredit(operation, details = {}) {
    try {
        const userData = getUserData();
        if (!userData || !userData.id) {
            throw new Error('User not authenticated');
        }
                
        // Update credit record with new transaction
        const result = await apiRequest('POST', '/api/index/useUserCredits', {
            type: operation,
            details: details
        });

        if (result.success == false) {
            throw new Error(result.error)
        }
        
        // Dispatch credit update event
        dispatchCreditUpdateEvent(result.data.value);
        
        return {
            success: true,
            remainingCredits: result.data.value,
            operation
        };
    } catch (error) {
        console.error('Failed to use credit:', error);
        throw error;
    }
}

function getOperationName(operation) {
    const operationNames = {
        'youtube_to_text': 'YouTube 轉文字',
        'rewrite_content': '內容重寫',
        'generate_voice': '生成 AI 語音',
        'voice_clone': '語音克隆',
        'pdf_to_excel': 'PDF 轉 Excel',
        'website_scraper': '網站內容擷取',
        'purchase_credits': '購買點數'
    };
    
    return operationNames[operation] || operation;
}

// Helper function to dispatch credit update event
export function dispatchCreditUpdateEvent(credits) {
    const event = new CustomEvent('creditUpdate', {
        detail: { credits }
    });
    window.dispatchEvent(event);
}

// Add credits to user account
async function addCredits(userId, amount) {
    try {
        if (!userId) {
            const userData = getUserData();
            if (!userData || !userData.id) {
                throw new Error('User not authenticated');
            }
            userId = userData.id;
        }
        
        const creditData = await getUserCredits(userId);
        const newCredits = creditData.credits + amount;
        
        const transaction = {
            operation: 'purchase_credits',
            timestamp: new Date().toISOString(),
            details: { amount }
        };
        
        const history = [...(creditData.history || []), transaction];
        
        await trickleUpdateObject('user_credits', creditData.id, {
            userId,
            credits: newCredits,
            history
        });
        
        // Dispatch credit update event
        dispatchCreditUpdateEvent(newCredits);
        
        return {
            success: true,
            totalCredits: newCredits
        };
    } catch (error) {
        console.error('Failed to add credits:', error);
        throw error;
    }
}

// Get credit history for a user
async function getCreditHistory(userId) {
    try {
        const creditData = await getUserCredits(userId);
        return creditData.history || [];
    } catch (error) {
        console.error('Failed to get credit history:', error);
        throw error;
    }
}

// Add credits after a successful Stripe payment
async function addCreditsAfterPayment(userId, paymentAmount, paymentDetails) {
    try {
        // Calculate credits based on payment amount
        const creditAmount = Math.floor(parseFloat(paymentAmount) * 10); // $1 = 10 credits
        
        // Add credits to user account
        const result = await addCredits(userId, creditAmount);
        
        // Record the payment transaction
        await recordPaymentTransaction(userId, paymentAmount, creditAmount, paymentDetails);
        
        return {
            success: true,
            credits: result.totalCredits,
            added: creditAmount
        };
    } catch (error) {
        console.error('Failed to add credits after payment:', error);
        throw error;
    }
}

// Record a payment transaction
async function recordPaymentTransaction(userId, amount, credits, paymentDetails) {
    try {
        const userData = getUserData();
        if (!userData) return;
        
        const transactionData = {
            userId: userId || userData.id,
            userEmail: userData.email,
            userName: userData.name,
            amount: parseFloat(amount),
            currency: 'HKD',
            credits: credits,
            paymentMethod: paymentDetails?.type || 'credit_card',
            paymentId: paymentDetails?.id || `manual_${Date.now()}`,
            status: 'succeeded',
            createdAt: new Date().toISOString()
        };
        
        await trickleCreateObject('payment_transactions', transactionData);
        return transactionData;
    } catch (error) {
        console.error('Failed to record payment transaction:', error);
        // Don't throw error here as this is not critical for the user experience
        return null;
    }
}
